import {
  Owner,
  Parameters,
  PatrimonyRepartition,
  PledgeRead,
  Product,
  ProductParameters,
} from '../../definition/interface/Pledge';

/* -------------------------------------------------------------------------- */
/*                                   CONSTANTS                                */
/* -------------------------------------------------------------------------- */

const ISIN_ATHENA_CONVICTION_MARS_2026 = 'FRIP00001WZ0';
const ISIN_LEADERS_TRANSATLANTIQUE_AVRIL_2026 = 'FR0014015WC2';

const MIN_INVESTMENT = 25_000;
const MIN_PATRIMONY = 750_000;
const MIN_FINANCIAL_KNOWLEDGE_SCORE = 186;

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */

type ValidationContext = {
  pledge: PledgeRead;
  mainProduct: Product;
  mainProductParameters: ProductParameters;
  owners: Owner[];
  parameters: Parameters;
};

type SupportRule = {
  isin: string;
  validate: (ctx: ValidationContext) => string[];
};

type SupportConfig = {
  isin: string;
  label: string;
};

/* -------------------------------------------------------------------------- */
/*                                ENTRY POINT                                 */
/* -------------------------------------------------------------------------- */

export const verifySwisslifeCustomPledgeRules = (
  pledge: PledgeRead,
): string[] => {
  if (!pledge) return [];

  const {
    products = [],
    operation = 'SUBSCRIPTION',
    owners = [],
    parameters = {} as Parameters,
  } = pledge;

  const mainProduct = products[0];
  if (!mainProduct || !isSwisslifeProduct(products)) return [];

  const ctx: ValidationContext = {
    pledge,
    mainProduct,
    mainProductParameters: mainProduct.parameters ?? ({} as ProductParameters),
    owners,
    parameters,
  };

  const supportRules: SupportRule[] = STRUCTURED_SUPPORTS.map(config =>
    createStructuredSupportRule(operation, config),
  );

  return supportRules.reduce<string[]>(
    (acc, rule) => acc.concat(rule.validate(ctx)),
    [],
  );
};

/* -------------------------------------------------------------------------- */
/*                               PRODUCT CHECK                                */
/* -------------------------------------------------------------------------- */

const isSwisslifeProduct = (products: Product[]): boolean =>
  products.some(({ parameters }) => {
    const manager = parameters?.manager?.name?.toUpperCase() ?? '';
    const partner = parameters?.partner?.name?.toUpperCase() ?? '';
    return (
      parameters?.partnerFlux === 'SWISSLIFE' ||
      manager.startsWith('SWISSLIFE') ||
      partner.startsWith('SWISSLIFE')
    );
  });

/* -------------------------------------------------------------------------- */
/*                             GENERIC VALIDATORS                             */
/* -------------------------------------------------------------------------- */

const validateMinAmount = (amount: number, message: string): string[] =>
  amount < MIN_INVESTMENT ? [message] : [];

const validateOwnersFinancialKnowledge = (
  owners: Owner[],
  config: SupportConfig,
): string[] => {
  const isValid = owners
    .map(o => o?.riskProfil?.connaissanceFinancierePoints ?? 0)
    .every(score => score >= MIN_FINANCIAL_KNOWLEDGE_SCORE);

  return isValid
    ? []
    : [
        `Le support ${config.label} n'est pas éligible si l'un des propriétaires n'a pas une connaissance/expérience financière approfondie`,
      ];
};

const validatePatrimony = (
  parameters: Parameters,
  config: SupportConfig,
): string[] => {
  const patrimony =
    (parameters?.patrimonyRepartition as PatrimonyRepartition)?.amount ?? 0;

  return patrimony < MIN_PATRIMONY
    ? [
        `Le support ${config.label} n'est pas éligible si le patrimoine net d'emprunt du foyer est inférieur à 750k€`,
      ]
    : [];
};

/* -------------------------------------------------------------------------- */
/*                          SUPPORT CONFIGURATION                             */
/* -------------------------------------------------------------------------- */

const STRUCTURED_SUPPORTS: SupportConfig[] = [
  {
    isin: ISIN_ATHENA_CONVICTION_MARS_2026,
    label: 'Athena Conviction Mars 2026',
  },
  {
    isin: ISIN_LEADERS_TRANSATLANTIQUE_AVRIL_2026,
    label: 'Leaders Transatlantique Rendement Avril 2026',
  },
];

/* -------------------------------------------------------------------------- */
/*                          RULE FACTORY (GENERIC)                            */
/* -------------------------------------------------------------------------- */

const createStructuredSupportRule = (
  operation: string,
  config: SupportConfig,
): SupportRule => ({
  isin: config.isin,
  validate: ctx => {
    const errors: string[] = [];

    switch (operation) {
      case 'SUBSCRIPTION':
        errors.push(...validateSubscription(ctx, config));
        break;

      case 'VERSEMENT_COMPLEMENTAIRE':
      case 'VERSEMENT_PROGRAMME':
        errors.push(...validateAdditionalPayment(ctx, config));
        break;

      case 'ARBITRAGE_INDIVIDUAL':
        errors.push(...validateArbitrage(ctx, config));
        break;
    }

    return errors;
  },
});

/* -------------------------------------------------------------------------- */
/*                         OPERATION-SPECIFIC LOGIC                           */
/* -------------------------------------------------------------------------- */

const validateSubscription = (
  ctx: ValidationContext,
  config: SupportConfig,
): string[] => {
  const { mainProductParameters, owners, parameters } = ctx;
  const errors: string[] = [];

  const {
    supportRepartition = [],
    avInvestmentType = 'INITIAL',
    avInitialPayment = 0,
  } = mainProductParameters;

  const support = supportRepartition.find(s => s?.isin === config.isin);
  if (!support) return [];

  if (avInvestmentType === 'MONTHLY') {
    return [
      `Le support ${config.label} n'est pas éligible en cas d'investissement programmé.`,
    ];
  }

  if (
    avInvestmentType === 'INITIAL_AND_MONTHLY' &&
    support?.monthlyRepartition > 0
  ) {
    errors.push(
      `Le support ${config.label} n'est pas éligible en cas d'investissement programmé.`,
    );
  }

  const investedAmount =
    support.supportRepartitionType === 'PERCENTAGE'
      ? avInitialPayment * (support.initalRepartition ?? 0)
      : (support.initalRepartition ?? 0);

  errors.push(
    ...validateMinAmount(
      investedAmount,
      `Le montant investi sur le support ${config.label} doit être supérieur ou égal à 25k€.`,
    ),
  );

  errors.push(...validateOwnersFinancialKnowledge(owners, config));
  errors.push(...validatePatrimony(parameters, config));

  return errors;
};

const validateAdditionalPayment = (
  ctx: ValidationContext,
  config: SupportConfig,
): string[] => {
  const { mainProductParameters, owners, parameters } = ctx;
  const errors: string[] = [];

  const support =
    mainProductParameters?.supportsInvestment?.supportRepartition?.find(
      s => s?.isin === config.isin,
    );
  if (!support) return [];

  errors.push(
    ...validateMinAmount(
      support.amount ?? 0,
      `Le montant investi sur le support ${config.label} doit être supérieur ou égal à 25k€.`,
    ),
  );

  errors.push(...validateOwnersFinancialKnowledge(owners, config));
  errors.push(...validatePatrimony(parameters, config));

  return errors;
};

const validateArbitrage = (
  ctx: ValidationContext,
  config: SupportConfig,
): string[] => {
  const { mainProductParameters, owners, parameters } = ctx;
  const errors: string[] = [];

  const { supportsInvestmentStock = [], supportsInvestmentFlux = [] } =
    mainProductParameters;

  const inFlux = supportsInvestmentFlux.some(s => s?.isin === config.isin);

  if (inFlux) {
    errors.push(
      `Le support ${config.label} n'est pas éligible en cas d'arbitrage sur le flux.`,
    );
  }

  const support = supportsInvestmentStock.find(s => s?.isin === config.isin);
  if (!support) return errors;

  errors.push(
    ...validateMinAmount(
      support.amountRepartitionStock ?? 0,
      `Le montant investi sur le support ${config.label} doit être supérieur ou égal à 25k€.`,
    ),
  );

  errors.push(...validateOwnersFinancialKnowledge(owners, config));
  errors.push(...validatePatrimony(parameters, config));

  return errors;
};
